import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronDown } from "lucide-react";
import * as React from "react";

interface MultiSelectProps {
    value: string[];
    onValueChange: (value: string[]) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    children?: React.ReactNode;
}

/**
 * Multi-seleção construída em cima de Popover + botões simples — de propósito, NÃO em cima do
 * Radix Select. O Radix Select é uma máquina de estado de valor ÚNICO: forçá-lo a virar
 * multi-select via handleValueChange (marcar/desmarcar batendo em value.includes) sempre teve um
 * bug de fundo — clicar de novo no item que é o "valor atual" do Root não conta como mudança de
 * valor pro Radix, então esse clique nunca chegava a disparar nada e esse item específico ficava
 * impossível de desmarcar. Um Popover com botões comuns não tem essa noção de "valor atual" — cada
 * clique é só um clique, sempre alterna.
 */
const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
    ({ className, children, value, onValueChange, placeholder, disabled }, ref) => {
        const [open, setOpen] = React.useState(false);

        const toggleValue = (v: string) => {
            if (value.includes(v)) {
                onValueChange(value.filter((x) => x !== v));
            } else {
                onValueChange([...value, v]);
            }
        };

        const items = React.Children.toArray(children).filter(
            (child): child is React.ReactElement<{ value: string; children?: React.ReactNode }> =>
                React.isValidElement(child)
        );

        return (
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        ref={ref}
                        type="button"
                        disabled={disabled}
                        className={cn(
                            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                            className
                        )}
                    >
                        <span className="truncate">
                            {value.length === 0
                                ? placeholder
                                : `${value.length} selecionado${value.length === 1 ? "" : "s"}`}
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="z-50 w-[--radix-popover-trigger-width] min-w-[8rem] max-h-96 overflow-y-auto p-1"
                >
                    {items.map((item) => {
                        const itemValue = item.props.value;
                        const selected = value.includes(itemValue);
                        return (
                            <button
                                key={itemValue}
                                type="button"
                                onClick={() => toggleValue(itemValue)}
                                className={cn(
                                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none",
                                    selected ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
                                )}
                            >
                                <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                                    {selected && <Check className="h-4 w-4" />}
                                </span>
                                <span className="truncate">{item.props.children}</span>
                            </button>
                        );
                    })}
                </PopoverContent>
            </Popover>
        );
    }
);
MultiSelect.displayName = "MultiSelect";

/** Só um descritor (value + label) lido pelo MultiSelect acima — nunca é renderizado sozinho. */
function SelectItem({ children }: { value: string; children?: React.ReactNode }) {
    return <>{children}</>;
}
SelectItem.displayName = "SelectItem";

export { MultiSelect, SelectItem };
